// Configuration defaults for Plan Designer component
// This file provides default configurations that can be overridden by external configuration

import { PlanDesignerConfiguration } from "./types/configuration.types";

/**
 * Deep merge utility for configuration objects
 * Recursively merges configuration objects, with override values taking precedence
 */
const deepMerge = <T extends Record<string, unknown>>(
  target: T,
  source: Partial<T>,
): T => {
  const result = { ...target };

  for (const key in source) {
    if (source[key] !== undefined && source[key] !== null) {
      if (
        typeof source[key] === "object" &&
        !Array.isArray(source[key]) &&
        source[key] !== null
      ) {
        result[key] = deepMerge(
          (result[key] || {}) as Record<string, unknown>,
          source[key] as Record<string, unknown>,
        ) as T[Extract<keyof T, string>];
      } else {
        result[key] = source[key] as T[Extract<keyof T, string>];
      }
    }
  }

  return result;
};

/**
 * Base configuration reflecting current PlanDesigner component defaults
 * These values match the current hardcoded behavior in the component
 */
const PLAN_DESIGNER_BASE_CONFIG: PlanDesignerConfiguration = {
  featureFlags: {
    enableReviewPanel: true,
    enableComments: true,
    enableContextMenu: true,
    enableColumnGrouping: true,
    enableCellEditing: true,
    enableUndoRedo: true,
    enableAutoSave: false, // Currently manual save only
    enableFullScreen: true,
    enableBenefitDefinitionsCache: true, // Currently using Map cache
    enableColumnToolPanel: true,
    enableCellSelection: true,
    enableKeyboardNavigation: true,
    enableTooltips: true,
    enableAnimations: true,
  },

  theme: {
    theme: "quartz", // Currently using themeQuartz
    colorScheme: "inherit",
    fontSize: 14, // Current hardcoded value
    headerFontSize: 14, // Current hardcoded value
    iconSize: 14, // Current hardcoded value
    spacing: 4, // Current hardcoded value
    columnBorder: true, // Currently enabled
  },

  data: {
    dataSources: {
      useLocalData: true, // Currently using mock data
      cacheTimeout: 0, // No timeout currently set
    },
    benefitDefinitions: {
      autoLoad: true, // Currently auto-loads
      exposedCoverageTypes: [], // Currently shows all available
      hiddenBenefits: [],
    },
  },

  grid: {
    preset: "minimal", // Currently using minimal preset
    autoHeight: true, // Currently enabled
    tooltipShowDelay: 500, // Current hardcoded value
    pagination: false, // Currently disabled
    minColumnWidth: 150, // Current hardcoded value
    autoSaveInterval: 10000, // Currently updates display every 10 seconds
    enableRowGrouping: false,
    rowSelectionMode: "none",
    cellSelection: {
      enableFillHandle: true, // Currently enabled
      fillHandleMode: "fill", // Current mode
    },
    defaultColDef: {
      resizable: true,
      sortable: false,
      filter: false,
      editable: true,
      suppressHeaderMenuButton: true,
      autoHeight: true,
    },
  },

  ui: {
    utilityBar: {
      enabled: true, //
      showProgress: true, // Currently showing progress indicator
      showLastSaved: true, // Currently showing last saved timestamp
      showCoverageSelector: true, // Currently showing coverage dropdown
      showFullScreenButton: true, // Currently showing fullscreen button
      showSaveButton: true, // Currently showing save button
    },
    sidePanels: {
      enableReviewPanel: true, // Currently enabled
      position: "right", // Current implementation
    },
    dialogs: {
      commentDialog: {
        width: 400, // Reasonable default
        height: 300, // Reasonable default
        maxCharacters: 500, // Reasonable default
      },
    },
  },

  performance: {
    virtualScrolling: {
      enabled: false, // Not currently implemented
      bufferSize: 10,
    },
    debounce: {
      searchDelay: 300,
      saveDelay: 1000,
    },
    memory: {
      maxCacheSize: 50, // Reasonable default for benefit definitions cache
      cacheCleanupInterval: 300000, // 5 minutes
    },
  },

  validation: {
    cellValidation: {
      enableRealTimeValidation: false, // Not currently implemented
      validationDelay: 500,
      showInlineErrors: true,
    },
    businessRules: {
      requiredFields: [],
      fieldDependencies: {},
      customValidators: {},
    },
  },

  environment: {
    env: "development",
    debug: false,
    logLevel: "info",
  },
};

/**
 * Configuration manager class for Plan Designer
 * Handles merging of external configurations with defaults
 */
export class PlanDesignerConfigurationManager {
  private static instance: PlanDesignerConfigurationManager;
  private configuration: PlanDesignerConfiguration;
  private externalConfig: Partial<PlanDesignerConfiguration> | null = null;

  private constructor() {
    this.configuration = { ...PLAN_DESIGNER_BASE_CONFIG };
  }

  /**
   * Get singleton instance of configuration manager
   */
  public static getInstance(): PlanDesignerConfigurationManager {
    if (!PlanDesignerConfigurationManager.instance) {
      PlanDesignerConfigurationManager.instance =
        new PlanDesignerConfigurationManager();
    }
    return PlanDesignerConfigurationManager.instance;
  }

  /**
   * Set external configuration that will override defaults
   * This method is designed to be called when external config is available
   */
  public setExternalConfiguration(
    config: Partial<PlanDesignerConfiguration>,
  ): void {
    this.externalConfig = config;
    this.configuration = deepMerge(
      PLAN_DESIGNER_BASE_CONFIG as Record<string, unknown>,
      config as Record<string, unknown>,
    ) as PlanDesignerConfiguration;
  }

  /**
   * Get the current merged configuration
   */
  public getConfiguration(): PlanDesignerConfiguration {
    return this.configuration;
  }

  /**
   * Get a specific configuration section
   */
  public getFeatureFlags(): NonNullable<
    PlanDesignerConfiguration["featureFlags"]
  > {
    return this.configuration.featureFlags ?? {};
  }

  public getThemeConfig(): NonNullable<PlanDesignerConfiguration["theme"]> {
    return this.configuration.theme ?? {};
  }

  public getDataConfig(): NonNullable<PlanDesignerConfiguration["data"]> {
    return this.configuration.data ?? {};
  }

  public getGridConfig(): NonNullable<PlanDesignerConfiguration["grid"]> {
    return this.configuration.grid ?? {};
  }

  public getUIConfig(): NonNullable<PlanDesignerConfiguration["ui"]> {
    return this.configuration.ui ?? {};
  }

  public getPerformanceConfig(): NonNullable<
    PlanDesignerConfiguration["performance"]
  > {
    return this.configuration.performance ?? {};
  }

  public getValidationConfig(): NonNullable<
    PlanDesignerConfiguration["validation"]
  > {
    return this.configuration.validation ?? {};
  }

  /**
   * Check if a specific feature is enabled
   */
  public isFeatureEnabled(
    feature: keyof NonNullable<PlanDesignerConfiguration["featureFlags"]>,
  ): boolean {
    return this.configuration.featureFlags?.[feature] ?? false;
  }

  /**
   * Get environment-specific settings
   */
  public getEnvironment(): NonNullable<
    PlanDesignerConfiguration["environment"]
  > {
    return this.configuration.environment ?? {};
  }

  /**
   * Reset configuration to defaults (useful for testing)
   */
  public resetToDefaults(): void {
    this.externalConfig = null;
    this.configuration = { ...PLAN_DESIGNER_BASE_CONFIG };
  }

  /**
   * Get debug information about current configuration
   */
  public getDebugInfo(): {
    hasExternalConfig: boolean;
    externalConfigKeys: string[];
    currentConfig: PlanDesignerConfiguration;
  } {
    return {
      hasExternalConfig: this.externalConfig !== null,
      externalConfigKeys: this.externalConfig
        ? Object.keys(this.externalConfig)
        : [],
      currentConfig: this.configuration,
    };
  }
}

/**
 * Convenience function to get the configuration manager instance
 */
export const getConfigurationManager = (): PlanDesignerConfigurationManager =>
  PlanDesignerConfigurationManager.getInstance();

/**
 * Convenience function to get current configuration
 */
export const getCurrentConfiguration = (): PlanDesignerConfiguration =>
  getConfigurationManager().getConfiguration();

/**
 * Convenience function to check if a feature is enabled
 */
export const isFeatureEnabled = (
  feature: keyof NonNullable<PlanDesignerConfiguration["featureFlags"]>,
): boolean => getConfigurationManager().isFeatureEnabled(feature);

/**
 * Pre-built configuration presets for common scenarios
 */
export const CONFIGURATION_PRESETS = {
  // Minimal configuration - only essential features
  minimal: {
    featureFlags: {
      enableReviewPanel: false,
      enableComments: false,
      enableContextMenu: false,
      enableColumnGrouping: true,
      enableCellEditing: true,
      enableUndoRedo: false,
      enableAutoSave: false,
      enableFullScreen: false,
      enableBenefitDefinitionsCache: true,
      enableColumnToolPanel: false,
      enableCellSelection: false,
      enableKeyboardNavigation: true,
      enableTooltips: false,
      enableAnimations: false,
    },
  } as Partial<PlanDesignerConfiguration>,

  // Read-only configuration - no editing capabilities
  readonly: {
    featureFlags: {
      enableCellEditing: false,
      enableComments: false,
      enableContextMenu: false,
      enableUndoRedo: false,
      enableAutoSave: false,
      enableCellSelection: false,
    },
    grid: {
      defaultColDef: {
        editable: false,
      },
    },
  } as Partial<PlanDesignerConfiguration>,

  // Performance optimized configuration
  performance: {
    featureFlags: {
      enableAnimations: false,
      enableTooltips: false,
    },
    performance: {
      virtualScrolling: {
        enabled: true,
        bufferSize: 20,
      },
      debounce: {
        searchDelay: 150,
        saveDelay: 500,
      },
    },
  } as Partial<PlanDesignerConfiguration>,

  // Development configuration with debugging enabled
  development: {
    environment: {
      env: "development" as const,
      debug: true,
      logLevel: "debug" as const,
    },
    featureFlags: {
      enableAutoSave: false, // Prevent auto-save during development
    },
  } as Partial<PlanDesignerConfiguration>,

  // Production configuration
  production: {
    environment: {
      env: "production" as const,
      debug: false,
      logLevel: "error" as const,
    },
    featureFlags: {
      enableAutoSave: true,
    },
    performance: {
      memory: {
        maxCacheSize: 100,
        cacheCleanupInterval: 600000, // 10 minutes
      },
    },
  } as Partial<PlanDesignerConfiguration>,
} as const;

/**
 * Apply a preset configuration
 */
export const applyConfigurationPreset = (
  presetName: keyof typeof CONFIGURATION_PRESETS,
): void => {
  const preset = CONFIGURATION_PRESETS[presetName];
  if (preset) {
    getConfigurationManager().setExternalConfiguration(preset);
  }
};

// Export the base configuration for reference
export { PLAN_DESIGNER_BASE_CONFIG };
